/**
 * Finance Dashboard
 * Displays P&L, Outlet Comparison, and Variance Analysis
 * with interactive charts and export options
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Building2,
  BarChart3,
  PieChart as PieChartIcon,
  Download,
  RefreshCw,
  Calendar,
  ChevronRight,
  AlertCircle,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  FileText,
  Loader2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import { cn } from '@/lib/utils';
import { useAuth } from '@/context/AuthContext';
import api from '@/services/api';

// Colors for charts
const COLORS = ['#f97316', '#22c55e', '#3b82f6', '#eab308', '#ef4444', '#8b5cf6'];

// Format currency
const formatCurrency = (value, currency = 'NOK') => {
  if (value === null || value === undefined) return '-';
  return new Intl.NumberFormat('nb-NO', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

// Format percentage
const formatPercent = (value) => {
  if (value === null || value === undefined) return '-';
  return `${Number(value).toFixed(1)}%`;
};

// Trend indicator component
const TrendIndicator = ({ value, inverted = false }) => {
  if (value === null || value === undefined) return <Minus className="w-4 h-4 text-gray-400" />;
  
  const isPositive = inverted ? value < 0 : value > 0;
  const isNegative = inverted ? value > 0 : value < 0;
  
  if (Math.abs(value) < 0.5) {
    return <Minus className="w-4 h-4 text-gray-400" />;
  }
  
  return isPositive ? (
    <ArrowUpRight className="w-4 h-4 text-green-500" />
  ) : (
    <ArrowDownRight className="w-4 h-4 text-red-500" />
  );
};

// KPI Card Component
const FinanceKPICard = ({ title, value, subtitle, change, icon: Icon, loading, variant = 'default' }) => {
  const variants = {
    default: 'bg-orange-50 text-orange-600',
    success: 'bg-green-50 text-green-600',
    warning: 'bg-yellow-50 text-yellow-600',
    danger: 'bg-red-50 text-red-600',
  };
  
  return (
    <Card data-testid={`kpi-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={cn("p-2 rounded-lg", variants[variant])}>
              <Icon className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xs text-gray-500 font-medium">{title}</p>
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin text-gray-400 mt-1" />
              ) : (
                <>
                  <p className="text-xl font-bold text-gray-900">{value}</p>
                  {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
                </>
              )}
            </div>
          </div>
          {change !== undefined && !loading && (
            <div className={cn(
              "flex items-center gap-1 text-sm font-medium",
              change > 0 ? "text-green-600" : change < 0 ? "text-red-600" : "text-gray-500"
            )}>
              <TrendIndicator value={change} />
              <span>{formatPercent(Math.abs(change))}</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default function FinanceDashboard() {
  const { user, currentOutlet } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pl');
  const [dateRange, setDateRange] = useState('month');
  const [selectedOutlet, setSelectedOutlet] = useState('all');
  
  // Data states
  const [plData, setPLData] = useState(null);
  const [comparisonData, setComparisonData] = useState(null);
  const [varianceData, setVarianceData] = useState(null);
  const [error, setError] = useState(null);

  // Get date range based on selection
  const getDateRange = useCallback(() => {
    const today = new Date();
    const end = today.toISOString().split('T')[0];
    let start;
    
    switch (dateRange) {
      case 'week':
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - 7);
        start = weekStart.toISOString().split('T')[0];
        break;
      case 'month':
        const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        start = monthStart.toISOString().split('T')[0];
        break;
      case 'quarter':
        const quarterMonth = Math.floor(today.getMonth() / 3) * 3;
        const quarterStart = new Date(today.getFullYear(), quarterMonth, 1);
        start = quarterStart.toISOString().split('T')[0];
        break;
      case 'year':
        const yearStart = new Date(today.getFullYear(), 0, 1);
        start = yearStart.toISOString().split('T')[0];
        break;
      default:
        start = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
    }
    
    return { start, end };
  }, [dateRange]);

  // Fetch P&L data
  const fetchPLData = useCallback(async () => {
    if (!currentOutlet?.id) return;
    
    try {
      const { start, end } = getDateRange();
      const response = await api.get(`/finance/reports/pl`, {
        params: {
          outlet_id: currentOutlet.id,
          start_date: start,
          end_date: end,
        }
      });
      setPLData(response.data);
    } catch (err) {
      console.error('Failed to fetch P&L data:', err);
      setError('Failed to load P&L report');
    }
  }, [currentOutlet?.id, getDateRange]);

  // Fetch outlet comparison data
  const fetchComparisonData = useCallback(async () => {
    try {
      const { start, end } = getDateRange();
      const response = await api.get(`/finance/reports/outlet-comparison`, {
        params: {
          start_date: start,
          end_date: end,
          comparison_type: dateRange === 'week' ? 'weekly' : 'monthly',
        }
      });
      setComparisonData(response.data);
    } catch (err) {
      console.error('Failed to fetch comparison data:', err);
      setError('Failed to load outlet comparison');
    }
  }, [getDateRange, dateRange]);

  // Fetch variance data
  const fetchVarianceData = useCallback(async () => {
    if (!currentOutlet?.id) return;
    
    try {
      const response = await api.get(`/finance/reports/variance/week-over-week`, {
        params: {
          outlet_id: currentOutlet.id,
        }
      });
      setVarianceData(response.data);
    } catch (err) {
      console.error('Failed to fetch variance data:', err);
      setError('Failed to load variance analysis');
    }
  }, [currentOutlet?.id]);

  // Fetch all data
  const fetchAllData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    await Promise.all([
      fetchPLData(),
      fetchComparisonData(),
      fetchVarianceData(),
    ]);
    
    setLoading(false);
  }, [fetchPLData, fetchComparisonData, fetchVarianceData]);

  useEffect(() => {
    // Only fetch if we have the necessary data
    if (currentOutlet?.id) {
      fetchAllData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOutlet?.id, dateRange]);

  // Export to CSV
  const exportToCSV = () => {
    let csvContent = '';
    
    if (activeTab === 'pl' && plData) {
      csvContent = 'Metric,Amount\n';
      csvContent += `Gross Sales,${plData.gross_sales}\n`;
      csvContent += `Net Sales,${plData.net_sales}\n`;
      csvContent += `Food Cost,${plData.food_cost}\n`;
      csvContent += `Gross Profit,${plData.gross_profit}\n`;
      csvContent += `Operating Profit,${plData.operating_profit}\n`;
    } else if (activeTab === 'comparison' && comparisonData) {
      csvContent = 'Outlet,Gross Sales,Transactions,Avg Ticket,Guests\n';
      comparisonData.outlets?.forEach(outlet => {
        csvContent += `${outlet.outlet_name},${outlet.gross_sales},${outlet.transaction_count},${outlet.average_ticket},${outlet.guest_count}\n`;
      });
    } else if (activeTab === 'variance' && varianceData) {
      csvContent = 'Metric,Current,Previous,Variance,Change %\n';
      csvContent += `Sales,${varianceData.current_period?.gross_sales},${varianceData.previous_period?.gross_sales},${varianceData.sales_variance?.variance},${varianceData.sales_variance?.variance_pct}\n`;
      csvContent += `Transactions,${varianceData.current_period?.transaction_count},${varianceData.previous_period?.transaction_count},${varianceData.transaction_variance?.variance},${varianceData.transaction_variance?.variance_pct}\n`;
    }
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `finance-report-${activeTab}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // P&L pie chart data
  const plPieData = plData ? [
    { name: 'Food Cost', value: Number(plData.food_cost) || 0 },
    { name: 'Labor', value: Number(plData.labor_cost) || 0 },
    { name: 'Rent', value: Number(plData.rent) || 0 },
    { name: 'Utilities', value: Number(plData.utilities) || 0 },
    { name: 'Marketing', value: Number(plData.marketing) || 0 },
    { name: 'Profit', value: Number(plData.operating_profit) || 0 },
  ].filter(item => item.value > 0) : [];

  // Outlet comparison bar chart data
  const comparisonBarData = comparisonData?.outlets?.map(outlet => ({
    name: outlet.outlet_name.split(' ')[0], // Shorten name
    sales: Number(outlet.gross_sales) || 0,
    transactions: outlet.transaction_count || 0,
    avgTicket: Number(outlet.average_ticket) || 0,
  })) || [];

  return (
    <div className="space-y-6" data-testid="finance-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Finance Dashboard</h1>
          <p className="text-gray-500 text-sm mt-1">
            Financial performance, P&L analysis, and outlet comparisons
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-[140px]" data-testid="date-range-select">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="quarter">This Quarter</SelectItem>
              <SelectItem value="year">This Year</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchAllData} data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={exportToCSV} data-testid="export-btn">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <span className="text-red-700">{error}</span>
            <Button variant="ghost" size="sm" onClick={fetchAllData}>Retry</Button>
          </CardContent>
        </Card>
      )}

      {/* KPI Summary Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <FinanceKPICard
          title="Gross Sales"
          value={formatCurrency(plData?.gross_sales)}
          icon={DollarSign}
          loading={loading}
        />
        <FinanceKPICard
          title="Gross Margin"
          value={formatPercent(plData?.gross_margin_pct)}
          subtitle={formatCurrency(plData?.gross_profit)}
          icon={TrendingUp}
          loading={loading}
          variant={plData?.gross_margin_pct >= 60 ? 'success' : plData?.gross_margin_pct >= 50 ? 'warning' : 'danger'}
        />
        <FinanceKPICard
          title="Operating Margin"
          value={formatPercent(plData?.operating_margin_pct)}
          subtitle={formatCurrency(plData?.operating_profit)}
          icon={BarChart3}
          loading={loading}
          variant={plData?.operating_margin_pct >= 15 ? 'success' : plData?.operating_margin_pct >= 8 ? 'warning' : 'danger'}
        />
        <FinanceKPICard
          title="Outlets"
          value={comparisonData?.total_outlets || '-'}
          subtitle={`${formatCurrency(comparisonData?.total_gross_sales)} total`}
          icon={Building2}
          loading={loading}
        />
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="pl" data-testid="tab-pl">
            <FileText className="w-4 h-4 mr-2" />
            P&L Statement
          </TabsTrigger>
          <TabsTrigger value="comparison" data-testid="tab-comparison">
            <Building2 className="w-4 h-4 mr-2" />
            Outlet Comparison
          </TabsTrigger>
          <TabsTrigger value="variance" data-testid="tab-variance">
            <TrendingUp className="w-4 h-4 mr-2" />
            Variance Analysis
          </TabsTrigger>
        </TabsList>

        {/* P&L Statement Tab */}
        <TabsContent value="pl" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* P&L Table */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Profit & Loss Statement</CardTitle>
                <CardDescription>
                  {plData?.outlet_name} | {plData?.period_start} to {plData?.period_end}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center h-64">
                    <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
                  </div>
                ) : (
                  <Table>
                    <TableBody>
                      {/* Revenue Section */}
                      <TableRow className="bg-gray-50 font-semibold">
                        <TableCell colSpan={2}>Revenue</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="pl-6">Gross Sales</TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(plData?.gross_sales)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="pl-6">Less: Discounts</TableCell>
                        <TableCell className="text-right text-red-600">
                          ({formatCurrency(plData?.discounts)})
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="pl-6">Less: Refunds</TableCell>
                        <TableCell className="text-right text-red-600">
                          ({formatCurrency(plData?.refunds)})
                        </TableCell>
                      </TableRow>
                      <TableRow className="border-t-2">
                        <TableCell className="font-semibold">Net Sales</TableCell>
                        <TableCell className="text-right font-bold">
                          {formatCurrency(plData?.net_sales)}
                        </TableCell>
                      </TableRow>
                      
                      {/* COGS Section */}
                      <TableRow className="bg-gray-50 font-semibold">
                        <TableCell colSpan={2}>Cost of Goods Sold</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="pl-6">Food Cost</TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(plData?.food_cost)}
                          <span className="text-xs text-gray-500 ml-2">
                            ({plData?.net_sales > 0 ? formatPercent((plData?.food_cost / plData?.net_sales) * 100) : '-'})
                          </span>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="pl-6">Beverage Cost</TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(plData?.beverage_cost)}
                        </TableCell>
                      </TableRow>
                      <TableRow className="border-t-2 bg-green-50">
                        <TableCell className="font-semibold">Gross Profit</TableCell>
                        <TableCell className="text-right font-bold text-green-600">
                          {formatCurrency(plData?.gross_profit)}
                          <span className="text-xs ml-2">({formatPercent(plData?.gross_margin_pct)})</span>
                        </TableCell>
                      </TableRow>
                      
                      {/* Operating Expenses */}
                      <TableRow className="bg-gray-50 font-semibold">
                        <TableCell colSpan={2}>Operating Expenses</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="pl-6">Labor</TableCell>
                        <TableCell className="text-right">{formatCurrency(plData?.labor_cost)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="pl-6">Rent</TableCell>
                        <TableCell className="text-right">{formatCurrency(plData?.rent)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="pl-6">Utilities</TableCell>
                        <TableCell className="text-right">{formatCurrency(plData?.utilities)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="pl-6">Marketing</TableCell>
                        <TableCell className="text-right">{formatCurrency(plData?.marketing)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="pl-6">Other</TableCell>
                        <TableCell className="text-right">{formatCurrency(plData?.other_expenses)}</TableCell>
                      </TableRow>
                      <TableRow className="border-t-2">
                        <TableCell className="font-semibold">Total OPEX</TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(plData?.total_opex)}
                        </TableCell>
                      </TableRow>
                      
                      {/* Operating Profit */}
                      <TableRow className="border-t-2 bg-orange-50">
                        <TableCell className="font-bold text-lg">Operating Profit</TableCell>
                        <TableCell className={cn(
                          "text-right font-bold text-lg",
                          plData?.operating_profit >= 0 ? "text-green-600" : "text-red-600"
                        )}>
                          {formatCurrency(plData?.operating_profit)}
                          <span className="text-sm ml-2">({formatPercent(plData?.operating_margin_pct)})</span>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            {/* Cost Breakdown Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Cost & Profit Breakdown</CardTitle>
                <CardDescription>Distribution of revenue</CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center h-64">
                    <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
                  </div>
                ) : plPieData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={plPieData}
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      >
                        {plPieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-64 text-gray-500">
                    No data available
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Outlet Comparison Tab */}
        <TabsContent value="comparison" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Comparison Table */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Outlet Performance</CardTitle>
                <CardDescription>
                  {comparisonData?.period_start} to {comparisonData?.period_end}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center h-64">
                    <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Outlet</TableHead>
                        <TableHead className="text-right">Sales</TableHead>
                        <TableHead className="text-right">Txns</TableHead>
                        <TableHead className="text-right">Avg Ticket</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {comparisonData?.outlets?.map((outlet, idx) => (
                        <TableRow key={outlet.outlet_id}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {idx < 3 && (
                                <Badge variant={idx === 0 ? "default" : "outline"} className="w-5 h-5 p-0 flex items-center justify-center text-xs">
                                  {idx + 1}
                                </Badge>
                              )}
                              <span className="font-medium">{outlet.outlet_name}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(outlet.gross_sales)}
                          </TableCell>
                          <TableCell className="text-right">{outlet.transaction_count}</TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(outlet.average_ticket)}
                          </TableCell>
                        </TableRow>
                      ))}
                      {/* Total Row */}
                      <TableRow className="bg-gray-50 font-semibold">
                        <TableCell>Total ({comparisonData?.total_outlets} outlets)</TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(comparisonData?.total_gross_sales)}
                        </TableCell>
                        <TableCell className="text-right">{comparisonData?.total_transactions}</TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(comparisonData?.overall_avg_ticket)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            {/* Comparison Bar Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Sales by Outlet</CardTitle>
                <CardDescription>Visual comparison of outlet performance</CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center h-64">
                    <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
                  </div>
                ) : comparisonBarData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={comparisonBarData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                      <YAxis type="category" dataKey="name" width={80} />
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Bar dataKey="sales" fill="#f97316" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-64 text-gray-500">
                    No data available
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Rankings */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-500">Top by Sales</CardTitle>
              </CardHeader>
              <CardContent>
                <ol className="space-y-2">
                  {comparisonData?.top_by_sales?.map((name, idx) => (
                    <li key={idx} className="flex items-center gap-2">
                      <Badge variant={idx === 0 ? "default" : "outline"} className="w-6 h-6 p-0 flex items-center justify-center">
                        {idx + 1}
                      </Badge>
                      <span className="font-medium">{name}</span>
                    </li>
                  ))}
                </ol>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-500">Top by Efficiency</CardTitle>
              </CardHeader>
              <CardContent>
                <ol className="space-y-2">
                  {comparisonData?.top_by_efficiency?.map((name, idx) => (
                    <li key={idx} className="flex items-center gap-2">
                      <Badge variant={idx === 0 ? "default" : "outline"} className="w-6 h-6 p-0 flex items-center justify-center">
                        {idx + 1}
                      </Badge>
                      <span className="font-medium">{name}</span>
                    </li>
                  ))}
                </ol>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-500">Total Guests</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-orange-600">
                  {comparisonData?.total_guests?.toLocaleString() || '-'}
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Across all outlets
                </p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Variance Analysis Tab */}
        <TabsContent value="variance" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Variance Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Week-over-Week Variance</CardTitle>
                <CardDescription>
                  {varianceData?.current_period?.period_start} to {varianceData?.current_period?.period_end} vs Previous Week
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center h-64">
                    <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Overall Trend */}
                    <div className={cn(
                      "p-4 rounded-lg text-center",
                      varianceData?.overall_trend === 'improving' ? "bg-green-50" :
                      varianceData?.overall_trend === 'declining' ? "bg-red-50" : "bg-gray-50"
                    )}>
                      <span className={cn(
                        "text-lg font-semibold capitalize",
                        varianceData?.overall_trend === 'improving' ? "text-green-600" :
                        varianceData?.overall_trend === 'declining' ? "text-red-600" : "text-gray-600"
                      )}>
                        {varianceData?.overall_trend || 'Stable'} Trend
                      </span>
                    </div>
                    
                    {/* Variance Table */}
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Metric</TableHead>
                          <TableHead className="text-right">Current</TableHead>
                          <TableHead className="text-right">Previous</TableHead>
                          <TableHead className="text-right">Change</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        <TableRow>
                          <TableCell className="font-medium">Sales</TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(varianceData?.current_period?.gross_sales)}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(varianceData?.previous_period?.gross_sales)}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <TrendIndicator value={varianceData?.sales_variance?.variance_pct} />
                              <span className={cn(
                                varianceData?.sales_variance?.trend === 'up' ? "text-green-600" :
                                varianceData?.sales_variance?.trend === 'down' ? "text-red-600" : ""
                              )}>
                                {formatPercent(varianceData?.sales_variance?.variance_pct)}
                              </span>
                            </div>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="font-medium">Transactions</TableCell>
                          <TableCell className="text-right">
                            {varianceData?.current_period?.transaction_count}
                          </TableCell>
                          <TableCell className="text-right">
                            {varianceData?.previous_period?.transaction_count}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <TrendIndicator value={varianceData?.transaction_variance?.variance_pct} />
                              <span className={cn(
                                varianceData?.transaction_variance?.trend === 'up' ? "text-green-600" :
                                varianceData?.transaction_variance?.trend === 'down' ? "text-red-600" : ""
                              )}>
                                {formatPercent(varianceData?.transaction_variance?.variance_pct)}
                              </span>
                            </div>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="font-medium">Avg Ticket</TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(varianceData?.current_period?.average_ticket)}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(varianceData?.previous_period?.average_ticket)}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <TrendIndicator value={varianceData?.ticket_variance?.variance_pct} />
                              <span className={cn(
                                varianceData?.ticket_variance?.trend === 'up' ? "text-green-600" :
                                varianceData?.ticket_variance?.trend === 'down' ? "text-red-600" : ""
                              )}>
                                {formatPercent(varianceData?.ticket_variance?.variance_pct)}
                              </span>
                            </div>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="font-medium">Guests</TableCell>
                          <TableCell className="text-right">
                            {varianceData?.current_period?.guest_count}
                          </TableCell>
                          <TableCell className="text-right">
                            {varianceData?.previous_period?.guest_count}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <TrendIndicator value={varianceData?.guest_variance?.variance_pct} />
                              <span className={cn(
                                varianceData?.guest_variance?.trend === 'up' ? "text-green-600" :
                                varianceData?.guest_variance?.trend === 'down' ? "text-red-600" : ""
                              )}>
                                {formatPercent(varianceData?.guest_variance?.variance_pct)}
                              </span>
                            </div>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Insights */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">AI Insights</CardTitle>
                <CardDescription>Automated analysis and recommendations</CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center h-64">
                    <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
                  </div>
                ) : (
                  <div className="space-y-4">
                    {varianceData?.insights?.length > 0 ? (
                      varianceData.insights.map((insight, idx) => (
                        <div key={idx} className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
                          <ChevronRight className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                          <p className="text-sm text-blue-800">{insight}</p>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <AlertCircle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                        <p>No significant changes detected this period.</p>
                        <p className="text-sm mt-1">Check back when more data is available.</p>
                      </div>
                    )}
                    
                    {/* Quick Stats */}
                    <div className="grid grid-cols-2 gap-4 mt-6 pt-4 border-t">
                      <div className="text-center">
                        <p className="text-xs text-gray-500">Current Week</p>
                        <p className="text-lg font-bold text-orange-600">
                          {formatCurrency(varianceData?.current_period?.gross_sales)}
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-gray-500">Previous Week</p>
                        <p className="text-lg font-bold text-gray-600">
                          {formatCurrency(varianceData?.previous_period?.gross_sales)}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
